import React, { useState, useMemo } from "react";
import "./Home.css";
import { getStoredData } from "../../api/fetchData.jsx";
import AdminFunctions from "../../components/AdminFunctions/AdminFunctions";
import RightUpdatesPanel from "../../components/RightUpdatesPanel/RightUpdatesPanel.jsx";

const Home = () => {
  const Function = getStoredData()?.[0]?.Functions || [];

  // Read user details & assigned projects from localStorage
  const userDetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetails") || "{}");
    } catch {
      return {};
    }
  }, []);

  const assignedProjects = Array.isArray(userDetails.project)
    ? userDetails.project
    : [];

  // 🔍 Search term for projects
  const [searchTerm, setSearchTerm] = useState("");

  // Selected project ("" = All Projects)
  const [selectedProject, setSelectedProject] = useState(
    localStorage.getItem("selectedProject") || ""
  );

  // View mode: "select" => project cards, "dashboard" => AdminFunctions
  const [viewMode, setViewMode] = useState(
    selectedProject ? "dashboard" : "select"
  );

  const handleProjectSelect = (projectId) => {
    setSelectedProject(projectId);
    localStorage.setItem("selectedProject", projectId || "");
    setViewMode("dashboard");
  };

  const handleBackToProjects = () => {
    setViewMode("select");
    // If you want to clear project on back, uncomment:
    // setSelectedProject("");
    // localStorage.setItem("selectedProject", "");
  };

  const activeProject =
    selectedProject && assignedProjects.find((p) => p._id === selectedProject);

  // 🔍 Filtered projects based on search term
  const filteredProjects = useMemo(() => {
    if (!searchTerm.trim()) return assignedProjects;
    const term = searchTerm.toLowerCase();

    return assignedProjects.filter((p) => {
      const name = p.projectName?.toLowerCase() || "";
      const id = p.projectId?.toLowerCase() || "";
      const company = p.company?.toLowerCase() || "";
      const location = p.siteLocation?.toLowerCase() || "";

      return (
        name.includes(term) ||
        id.includes(term) ||
        company.includes(term) ||
        location.includes(term)
      );
    });
  }, [assignedProjects, searchTerm]);

  return (
    <div className="admin-dashboard">
      <RightUpdatesPanel className="right-updates" />

      <div className="home-content-container">
        {/* ============ PROJECT SELECTION VIEW ============ */}
        {viewMode === "select" && (
          <>
            <div className="home-header">
              <div>
                <h1 className="home-title">
                  {userDetails?.name
                    ? `Welcome, ${userDetails.name.split(" ")[0]}`
                    : "Project Dashboard"}
                </h1>
                <p className="home-subtitle">
                  Select a project to open its dashboard. You can also choose{" "}
                  <strong>All Projects</strong> for a portfolio-level view.
                </p>
              </div>
            </div>

            <div className="project-section">
              {/* Header row with title + search */}
              <div className="project-section-header">
                <h2 className="section-title">Your Projects</h2>

                {assignedProjects.length > 0 && (
                  <div className="project-search">
                    <i className="fas fa-search project-search-icon" />
                    <input
                      type="text"
                      className="project-search-input"
                      placeholder="Search by name, ID, company, location..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                )}
              </div>

              {assignedProjects.length === 0 ? (
                <div className="no-project-message">
                  No projects are currently assigned to your account.
                  <br />
                  Please contact your administrator.
                </div>
              ) : (
                <>
                  {/* If search has no results */}
                  {filteredProjects.length === 0 ? (
                    <div className="no-project-message">
                      No projects match your search.
                    </div>
                  ) : (
                    <div className="project-cards-container">
                      {/* All Projects Card */}
                      <div
                        className={
                          "project-card project-card--all" +
                          (selectedProject === "" ? " active" : "")
                        }
                        onClick={() => handleProjectSelect("")}
                      >
                        <div className="project-card-header">
                          <span className="project-card-pill">Portfolio</span>
                        </div>
                        <h3>All Projects</h3>
                        <p>
                          View modules and counts across every assigned project.
                        </p>
                      </div>

                      {/* Individual Project Cards */}
                      {filteredProjects.map((p) => (
                        <div
                          key={p._id}
                          className={
                            "project-card" +
                            (selectedProject === p._id ? " active" : "")
                          }
                          onClick={() => handleProjectSelect(p._id)}
                        >
                          <div className="project-card-header">
                            {p.company && (
                              <span className="project-card-pill">
                                {p.company.slice(0, 26)}
                              </span>
                            )}
                          </div>

                          <h3>{p.projectName}</h3>

                          {p.siteLocation && (
                            <p className="project-meta">{p.siteLocation}</p>
                          )}

                          {p.projectId && (
                            <p className="project-id">
                              Project ID: {p.projectId}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {/* ============ DASHBOARD VIEW (AFTER SELECTION) ============ */}
        {viewMode === "dashboard" && (
          <div className="dashboard-section">
            <div className="dashboard-header">
              {/* Breadcrumb */}
              <div className="dashboard-breadcrumb">
                <span
                  className="breadcrumb-link"
                  onClick={handleBackToProjects}
                >
                  Projects
                </span>

                {selectedProject !== "" && (
                  <>
                    <span className="breadcrumb-separator">/</span>
                    <span className="breadcrumb-current">
                      {activeProject?.projectName || "Project"}
                    </span>
                  </>
                )}
              </div>

              {/* Title Row */}
              <div className="dashboard-header-row">
                <h2 className="dashboard-titleMain">Project Overview</h2>

                {selectedProject !== "" && activeProject?.siteLocation && (
                  <span className="dashboard-chip">
                    {activeProject.siteLocation}
                  </span>
                )}
              </div>
            </div>

            <AdminFunctions
              functions={Function}
              selectedProject={selectedProject}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
